import React, { useState, useEffect } from 'react';
import { Timer } from 'lucide-react';

interface CountdownTimerProps {
  targetDate: Date;
}

interface TimeRemaining {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
  isPast: boolean;
}

const CountdownTimer: React.FC<CountdownTimerProps> = ({ targetDate }) => {
  const [timeRemaining, setTimeRemaining] = useState<TimeRemaining>(() => {
    return calculateTimeRemaining(targetDate);
  });

  useEffect(() => {
    const interval = setInterval(() => {
      const remaining = calculateTimeRemaining(targetDate);
      setTimeRemaining(remaining);
    }, 1000);

    return () => clearInterval(interval);
  }, [targetDate]);

  function calculateTimeRemaining(target: Date): TimeRemaining {
    const now = new Date().getTime();
    const targetTime = target.getTime();
    const difference = targetTime - now;

    if (difference <= 0) {
      return {
        days: 0,
        hours: 0,
        minutes: 0,
        seconds: 0,
        isPast: true,
      };
    }

    const days = Math.floor(difference / (1000 * 60 * 60 * 24));
    const hours = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((difference % (1000 * 60)) / 1000);

    return {
      days,
      hours,
      minutes,
      seconds,
      isPast: false,
    };
  }

  if (timeRemaining.isPast) {
    return null;
  }

  return (
    <div className="mt-2 flex items-center gap-1.5 text-xs text-gray-600">
      <Timer className="w-3 h-3" />
      <span className="font-medium">
        {timeRemaining.days > 0 && `${timeRemaining.days}d `}
        {timeRemaining.hours > 0 && `${timeRemaining.hours}h `}
        {timeRemaining.minutes > 0 && `${timeRemaining.minutes}m `}
        {timeRemaining.seconds}s
      </span>
    </div>
  );
};

export default CountdownTimer;
